import React, {useEffect, useState} from "react";
import {useTitleFromComponent} from "../../utils/hooks";
import { useVsiAppContext} from "../../stores/VsiApp";
import {VsiH4, VsiHr, VsiSpanText} from "../../styled/App.styled";
import {
    Button,
    Col,
    Form,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Row,
    Spinner
} from "reactstrap";
import {autobotDateOptions, unitPreferenceList} from "../../utils/utils";
import RequiredAsterisk from "../../components/RequiredAsterisk";
import {
    autobotDateFormatter,
    booleanFormatter,
    downloadStatusFormatter,
    shortDatePlusTimeFormatter,
    uomPreference,
    runGrowthRateButtonFormatter, RunAutobotBtnFormatter, RunGrowthRateButtonFormatter
} from "../../utils/gridFormatters";
import Grid from "../../components/Grid";
import {DeleteAuth, GetAuth, PostAuth, PutAuth} from "../../utils/Verbs";
import {toSnakeCase} from "../../utils/Helpers";
import VsiLabeledSelect from "../../components/VsiLabeledSelect";
import swal from "sweetalert";
import axios from "axios";

const clientDefs = [
    {
        field: "clientId",
        headerName: "Client ID"
    },
    {
        field: "name",
        headerName: "Client Name",
        filter: 'agSetColumnFilter',
    },
    {
        field: "uploadFolder",
        headerName: 'Upload Folder'
    },
    {
        field: "lastuploaddate",
        headerName: 'Last Update Date',
        hide: true
    },
    {
        field: "uomPreference",
        headerName: 'UOM Preference',
        cellRenderer: uomPreference
    },
    {
        field: "isEnabled",
        headerName: "Is Enabled",
        filter: 'agSetColumnFilter',
        cellRenderer: booleanFormatter,
    },
    {
        field: "showWeeklyDevices",
        headerName: "Show Weekly Devices",
        sortable: true,
        filter: 'agSetColumnFilter',
        cellRenderer: booleanFormatter,
    },
    {
        field: "isAutobot",
        headerName: "Use Autobot?",
        sortable: true,
        filter: 'agSetColumnFilter',
        cellRenderer: booleanFormatter,
    },
    {
        field: "autobotDay",
        headerName: 'Scheduled Autobot Date',
        cellRenderer: autobotDateFormatter
    },
    {
        field: "processStatus",
        headerName: 'Status',
        cellRenderer: downloadStatusFormatter
    },
    {
        field: "version5ClientId",
        headerName: 'V5 Client ID',
        sortable: true,
        filter: 'agSetColumnFilter',

    },
    {
        field: "version5ClientId",
        headerName: "V5 Client ID",
        sortable: true,
        hide: true
    },
    {
        field: "lastVsiClientSession",
        headerName: "Last VSI Client Session",
        sortable: true,
        cellRenderer: shortDatePlusTimeFormatter
    },
    {
        field: "clientId",
        headerName: "Autobot",
        resizable: true,
        aggFunc: (params) => {
            if (params.rowNode) {
                return params.rowNode.key;
            } else {
                return params;
            }
        },
        cellRenderer: RunAutobotBtnFormatter
    },
    {
        field: "runGrowthCalcs",
        headerName: "Growth Calcs",
        // need to figure out how to pass the params in here
        cellRenderer: RunGrowthRateButtonFormatter
    }
];

const defaultClient = {
    "clientId": 0,
    "name": "",
    "autobotDay": 1,
    "isEnabled": true,
    "capacityThreshold": 80,
    "performanceThreshold": 80,
    "parentClientId": 0,
    "uploadFolder": "",
    "isAutobot": true,
    "isAutobotMonthly": true,
    "showWeeklyDevices": true,
    "processStatus": 0,
    "uomPreference": "GB",
    "serviceNowInstance": null,
    "serviceNowUser": null,
    "serviceNowPassword": null,
    "version5ClientId": null,
    "synchronizeUploads": false
};

export default function Clients({title}) {
    useTitleFromComponent(title);
    const { theme, client, user, getAccessTokenSilently } = useVsiAppContext();
    const [isLoading, setIsLoading] = useState(true);
    const [allClients, setAllClients] = useState([]);
    const [clientSelected, setSelectClient] = useState(defaultClient);
    const [newClient, setNewClient] = useState(defaultClient);
    const [isEditClientOpen, setEditClientOpen] = useState(false);
    const [isAddClientOpen, setAddClientOpen] = useState(false);
    const [refresh, setRefresh] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const toggleAddClient = () => {
        setAddClientOpen(!isAddClientOpen);
    };

    const toggleDeleteModal = () => {
        setShowDeleteModal(!showDeleteModal);
    };

    const toggleEditClient = (doubleClickedRow) => {
        if (doubleClickedRow?.data) {
            setSelectClient(doubleClickedRow.data);
        } else {
            setSelectClient(defaultClient);
        }
        setEditClientOpen(!isEditClientOpen);
    };


    const setUom = (e, process) => {
        if (process === "editClient") {
            setSelectClient({
                ...clientSelected,
                uomPreference: e.value
            });
        } else {
            setNewClient({
                ...newClient,
                uomPreference: e.value
            });
        }
    };

    const setAutobotDate = (e, process) => {
        if (process === "editClient") {
            setSelectClient({
                ...clientSelected,
                autobotDay: e.value
            });
        } else {
            setNewClient({
                ...newClient,
                autobotDay: e.value
            });
        }
    };

    const onValueChecked = (e, type) => {
        const {name, checked} = e.target;
        if (type === "add") {
            setNewClient({
                ...newClient,
                [name]: checked,
            });
        } else {
            setSelectClient({
                ...clientSelected,
                [name]: checked,
            });
        }
        ;
    };

    const onInputChange = (e, type) => {
        const {name, value} = e.target;
        if (type === "add") {
            setNewClient({
                ...newClient,
                [name]: value,
            });
        } else {
            setSelectClient({
                ...clientSelected,
                [name]: value,
            });
        }
    };

    const addClient = async () => {
        if (!client.uploadFolder) {
            var clientCopy = {...newClient, uploadFolder: toSnakeCase(newClient.name.trim())}
        }
        const token = await getAccessTokenSilently();
        PostAuth("/api/v6/admin/clients", clientCopy || newClient, token)
            .then(data => {
                if (data.status === 201) {
                    setAddClientOpen(!isAddClientOpen);
                    // Reset form
                    setNewClient(defaultClient);
                    setRefresh(!refresh);
                } else {
                    console.error(data);
                }
            })
            .catch(err => {
                console.error(err);
            });
    };

    const editClient = async () => {
        const token = await getAccessTokenSilently();
        PutAuth(`/api/v6/admin/clients/${clientSelected.clientId}`, clientSelected, token)
            .then(data => {
                if (data.status === 200) {
                    setEditClientOpen(!isEditClientOpen);
                    setRefresh(!refresh);
                } else {
                    console.error(data);
                }
            })
            .catch(err => {
                console.error(err);
            });
    };

    const deleteClient = async () => {
        toggleDeleteModal();
        toggleEditClient();
        setIsLoading(true);
        const token = await getAccessTokenSilently();
        DeleteAuth(`/api/v6/admin/clients/${clientSelected.clientId}`, token)
            .then(data => {
                if (data.status === 200) {
                    const formData = new FormData();
                    formData.append("client_id", clientSelected.clientId);
                    const config = {
                        headers: {
                            "content-type": "application/json",
                            Authorization: `Bearer ${token}`
                        }
                    };
                    axios.post('/api/v6/upload/delete_client', formData, config)
                        .then(async data => {
                            setEditClientOpen(!isEditClientOpen);
                            setRefresh(!refresh);
                        });
                } else {
                    console.error(data);
                }
                setIsLoading(false);
            })
            .catch(err => {
                swal({
                    title: `Error deleting the client.`,
                    text: 'Please try again later.'
                });
                console.error(err);
                setIsLoading(false);
            });
    }

    useEffect(() => {
        const getClients = async () => {
            const token = await getAccessTokenSilently();
            GetAuth("/api/v6/admin/clients", token)
            .then(data => {
                let sorted = data.data.sort((firstClient, secondClient) => {
                    if (firstClient.name < secondClient.name) {
                        return -1;
                    } else if (firstClient.name > secondClient.name) {
                        return 1;
                    } else {
                        return 0;
                    }
                });

                setAllClients(sorted);
                setIsLoading(false);
            })
            .catch(err => {
                console.error(err);
                setIsLoading(false);
            })
        }
        getClients();
    }, [refresh]);

    return <div>
        <VsiH4>Clients</VsiH4>
        <VsiHr/>
        <div className={"py-2"}>
            <Button color="success" disabled={isLoading} onClick={toggleAddClient}>
                Add Client
            </Button>
        </div>
        {isLoading ? <Spinner color={"primary"}/> :
            <Grid data={allClients} columnDefs={clientDefs} suppressPivotMode rowDoubleClicked={toggleEditClient}/>
        }

        {/*This model is for ADDING*/}
        <Modal isOpen={isAddClientOpen} toggle={toggleAddClient} size={"xl"} className={`bg-${theme}`}>
            <ModalHeader toggle={toggleAddClient}
                         className={`bg-${theme} ${theme === 'dark' ? "text-light" : "text-dark"}`}>Add
                Client</ModalHeader>
            <ModalBody className={`bg-${theme} ${theme === 'dark' ? "text-light" : "text-dark"}`}>
                <Form>
                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="companyName">
                                    Company Name <RequiredAsterisk/>
                                </Label>
                                <Input
                                    id="companyName"
                                    name="name"
                                    placeholder="A Company"
                                    type="text"
                                    onChange={e => {
                                        onInputChange(e, "add")
                                    }}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label for="uploadFolder">
                                    Upload Folder
                                </Label>
                                <Input
                                    id="uploadFolder"
                                    name="uploadFolder"
                                    placeholder="Upload Folder"
                                    defaultValue={toSnakeCase(newClient.name.trim())}
                                    type="text"
                                    onChange={e => {
                                        onInputChange(e, "add")
                                    }}
                                />
                            </FormGroup>
                            <Row>
                                <Col>
                                    <FormGroup>
                                        <Label for="performanceThreshold">
                                            Performance Threshold (%) <RequiredAsterisk/>
                                        </Label>
                                        <Input
                                            id="performanceThreshold"
                                            name="performanceThreshold"
                                            defaultValue={newClient.performanceThreshold}
                                            onChange={e => {
                                                onInputChange(e, "add")
                                            }}
                                            type="number"
                                        />
                                    </FormGroup>
                                </Col>
                                <Col>
                                    <FormGroup>
                                        <Label for="capacityThreshold">
                                            Capacity Threshold (%) <RequiredAsterisk/>
                                        </Label>
                                        <Input
                                            id="capacityThreshold"
                                            name="capacityThreshold"
                                            defaultValue={newClient.performanceThreshold}
                                            onChange={e => {
                                                onInputChange(e, "add")
                                            }}
                                            type="number"
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <FormGroup check>
                                        <Input type="checkbox"
                                               name="isEnabled"
                                               checked={newClient.isEnabled}
                                               onChange={(e) => {
                                                   onValueChecked(e, "add")
                                               }}/>
                                        {' '}
                                        <Label check>
                                            Active
                                        </Label>
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup check>
                                        <Input type="checkbox"
                                               name="showWeeklyDevices"
                                               checked={newClient.showWeeklyDevices}
                                               onChange={(e) => {
                                                   onValueChecked(e, "add")
                                               }}/>
                                        {' '}
                                        <Label check>
                                            Weekly Devices
                                        </Label>
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup check>
                                        <Input type="checkbox"
                                               name="isAutobot"
                                               checked={newClient.isAutobot}
                                               onChange={(e) => {
                                                   onValueChecked(e, "add")
                                               }}/>
                                        {' '}
                                        <Label check>
                                            Autobot
                                        </Label>
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup check>
                                        <Input type="checkbox"
                                               name="isAutobotMonthly"
                                               checked={newClient.isAutobotMonthly}
                                               onChange={(e) => {
                                                   onValueChecked(e, "add")
                                               }}/>
                                        {' '}
                                        <Label check>
                                            Autobot Monthly
                                        </Label>
                                    </FormGroup>
                                </Col>
                            </Row>
                        </Col>
                        <Col md={6}>
                            <FormGroup check>
                                <Input type="checkbox"
                                       name="synchronizeUploads"
                                       checked={newClient.syncrhonizeUploads}
                                       onChange={(e) => {
                                           onValueChecked(e, "add")
                                       }}/>
                                {' '}
                                <Label check>
                                    Sync Uploads?
                                </Label>
                            </FormGroup>
                            <FormGroup>
                                <Label for="capacityThreshold">
                                    V5 Client ID
                                </Label>
                                <Input
                                    id="version5ClientId"
                                    name="version5ClientId"
                                    defaultValue={newClient.version5ClientId}
                                    onChange={e => {
                                        onInputChange(e, "add")
                                    }}
                                    type="number"
                                />
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label htmlFor="uomPref">Unit Preference</Label>
                                <VsiLabeledSelect
                                    maxWidth='100%'
                                    options={unitPreferenceList}
                                    name="uomPref"
                                    onChange={e => {
                                        setUom(e)
                                    }}
                                    defaultValue={{value: 'GB', label: 'Gigabytes (GB)'}}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label htmlFor="autobot">Autobot Day</Label>
                                <VsiLabeledSelect
                                    maxWidth='100%'
                                    options={autobotDateOptions}
                                    name='autobot'
                                    isSearchable
                                    onChange={setAutobotDate}
                                    defaultValue={{value: 1, label: "Tuesday"}}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </Form>
            </ModalBody>
            <ModalFooter className={`bg-${theme} ${theme === 'dark' ? "text-light" : "text-dark"}`}>
                <Button color={"primary"} onClick={addClient}>
                    Add
                </Button>
                <Button onClick={toggleAddClient}>
                    Cancel
                </Button>
            </ModalFooter>
        </Modal>

        {/*This modal is for editing*/}
        <Modal isOpen={isEditClientOpen} toggle={toggleEditClient} size={"xl"} className={`bg-${theme}`}>
            <ModalHeader toggle={toggleEditClient}
                         className={`bg-${theme} ${theme === 'dark' ? "text-light" : "text-dark"}`}>Edit
                Client</ModalHeader>
            <ModalBody className={`bg-${theme} ${theme === 'dark' ? "text-light" : "text-dark"}`}>
                <Form>
                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="companyName">
                                    Company Name <RequiredAsterisk/>
                                </Label>
                                <Input
                                    id="companyName"
                                    name="name"
                                    placeholder="A Company"
                                    type="text"
                                    value={clientSelected.name}
                                    onChange={e => {
                                        onInputChange(e)
                                    }}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label for="uploadFolder">
                                    Upload Folder
                                </Label>
                                <Input
                                    id="uploadFolder"
                                    name="uploadFolder"
                                    type="text"
                                    value={clientSelected.uploadFolder}
                                    onChange={e => {
                                        onInputChange(e)
                                    }}
                                />
                            </FormGroup>
                            <Row>
                                <Col>
                                    <FormGroup>
                                        <Label for="performanceThreshold">
                                            Performance Threshold (%) <RequiredAsterisk/>
                                        </Label>
                                        <Input
                                            id="performanceThreshold"
                                            name="performanceThreshold"
                                            // placeholder="A Company"
                                            value={clientSelected.performanceThreshold}
                                            onChange={e => {
                                                onInputChange(e)
                                            }}
                                            type="number"
                                        />
                                    </FormGroup>
                                </Col>
                                <Col>
                                    <FormGroup>
                                        <Label for="capacityThreshold">
                                            Capacity Threshold (%) <RequiredAsterisk/>
                                        </Label>
                                        <Input
                                            id="capacityThreshold"
                                            name="capacityThreshold"
                                            // placeholder="A Company"
                                            value={clientSelected.capacityThreshold}
                                            onChange={e => {
                                                onInputChange(e)
                                            }}
                                            type="number"
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <FormGroup check>
                                        <Input type="checkbox"
                                               name="isEnabled"
                                               checked={clientSelected.isEnabled}
                                               onChange={(e) => {
                                                   onValueChecked(e)
                                               }}/>
                                        {' '}
                                        <Label check>
                                            Active
                                        </Label>
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup check>
                                        <Input type="checkbox"
                                               name="showWeeklyDevices"
                                               checked={clientSelected.showWeeklyDevices}
                                               onChange={(e) => {
                                                   onValueChecked(e)
                                               }}/>
                                        {' '}
                                        <Label check>
                                            Weekly Devices
                                        </Label>
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup check>
                                        <Input type="checkbox"
                                               name="isAutobot"
                                               checked={clientSelected.isAutobot}
                                               onChange={(e) => {
                                                   onValueChecked(e)
                                               }}/>
                                        {' '}
                                        <Label check>
                                            Autobot
                                        </Label>
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup check>
                                        <Input type="checkbox"
                                               name="isAutobotMonthly"
                                               checked={clientSelected.isAutobotMonthly}
                                               onChange={(e) => {
                                                   onValueChecked(e)
                                               }}/>
                                        {' '}
                                        <Label check>
                                            Autobot Monthly
                                        </Label>
                                    </FormGroup>
                                </Col>
                            </Row>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label htmlFor="uomPref">Unit Preference</Label>
                                <VsiLabeledSelect
                                    maxWidth='100%'
                                    options={unitPreferenceList}
                                    name="uomPref"
                                    onChange={e => {
                                        setUom(e)
                                    }}
                                    defaultValue={{value: 'GB', label: 'Gigabytes (GB)'}}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label htmlFor="autobot">Autobot Day</Label>
                                <VsiLabeledSelect
                                    maxWidth='100%'
                                    options={autobotDateOptions}
                                    name='autobot'
                                    isSearchable
                                    onChange={setAutobotDate}
                                    defaultValue={{value: 1, label: "Tuesday"}}
                                />
                            </FormGroup>
                            <FormGroup check>
                                <Label for={"synchronizeUploads"}>Sync uploads with V5</Label>
                                <Input type={"checkbox"}
                                       checked={clientSelected.synchronizeUploads}
                                       id={"synchronizeUploads"}
                                       name={"synchronizeUploads"}
                                       onChange={onValueChecked}></Input>
                            </FormGroup>
                            <FormGroup>
                                <Label for={"version5ClientId"}> V5 Client ID</Label>
                                <Input type={"text"}
                                       name={"version5ClientId"}
                                       id={"version5ClientId"}
                                       defaultValue={clientSelected.version5ClientId}
                                       onChange={onInputChange}></Input>
                            </FormGroup>
                            <FormGroup>
                                <Label for="serviceNowInstance">
                                    Service Now API
                                </Label>
                                <Input
                                    id="serviceNowInstance"
                                    name="serviceNowInstance"
                                    type="text"
                                    value={clientSelected.serviceNowInstance}
                                    onChange={e => {
                                        onInputChange(e)
                                    }}
                                />
                                <Label for="serviceNowUser">
                                    Service Now User
                                </Label>
                                <Input
                                    id="serviceNowUser"
                                    name="serviceNowUser"
                                    type="text"
                                    value={clientSelected.serviceNowUser}
                                    onChange={e => {
                                        onInputChange(e)
                                    }}
                                />
                                <Label for="serviceNowUser">
                                    Service Now Password
                                </Label>
                                <Input
                                    id="serviceNowPassword"
                                    name="serviceNowPassword"
                                    type="password"
                                    value={clientSelected.serviceNowPassword}
                                    onChange={e => {
                                        onInputChange(e)
                                    }}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </Form>
            </ModalBody>
            <ModalFooter className={`bg-${theme} ${theme === 'dark' ? "text-light" : "text-dark"}`}>
                <Button color={"primary"} onClick={editClient}>
                    Submit Changes
                </Button>
                <Button color={"danger"} onClick={toggleDeleteModal}>
                    Delete Client
                </Button>
                <Button onClick={toggleEditClient}>
                    Cancel
                </Button>
            </ModalFooter>
        </Modal>
        <Modal className="delete-modal" backdrop="static"
               isOpen={showDeleteModal}>
            <ModalHeader className={`bg-${theme}`} toggle={toggleDeleteModal}>
                <VsiSpanText>
                    Are you sure you want to delete this Client? <b>This action is irreversible.</b>
                </VsiSpanText>
            </ModalHeader>
            <ModalBody className={`bg-${theme}`}>
                <VsiSpanText>{clientSelected.clientId} - {clientSelected.name}</VsiSpanText>
            </ModalBody>
            <ModalFooter className={`bg-${theme}`}>
                <Button color="danger" onClick={deleteClient}>Yes</Button>
                <Button color="secondary" onClick={toggleDeleteModal}>No</Button>
            </ModalFooter>
        </Modal>
    </div>;
}
